import { useTranslation } from "react-i18next"

import { Direction } from "@/generated/TradingGateway"
import { CurrencyPair } from "@/services/currencyPairs"

import { useIsNotionalValid } from "../NotionalInput"
import { sendExecution } from "../tile.state"
import { useTileCurrencyPair } from "../TileContext"
import { QuoteStateStage, useIsRfq, useRfqState } from "../TileFooter/RfqTimer"
import {
  PriceButtonButton as Button,
  PriceButtonPrice as Price,
  PriceButtonSymbol as Symbol,
  PriceButtonVariant,
} from "./components"
import { usePrice } from "./priceButton.state"

export interface PriceButtonProps {
  direction: Direction
}

export const PriceButton = ({ direction }: PriceButtonProps) => {
  const rfqState = useRfqState()
  const currencyPair = useTileCurrencyPair()
  const { price, isExpired } = usePrice(currencyPair.symbol, direction)
  const isRfq = useIsRfq()
  const isNotionalValid = useIsNotionalValid()
  const disabled =
    price === 0 ||
    !isNotionalValid ||
    (isRfq && rfqState.stage !== QuoteStateStage.Received)

  let variant: PriceButtonVariant = "primary"
  if (rfqState.stage === QuoteStateStage.Requested) {
    variant = "pending"
  } else if (rfqState.stage === QuoteStateStage.Received) {
    variant = "priceAnnounced"
  } else if (disabled) {
    variant = "disabled"
  }

  return (
    <PriceButtonInner
      direction={direction}
      variant={variant}
      currencyPair={currencyPair}
      isExpired={isExpired}
      price={price}
    />
  )
}

export interface PriceButtonInnerProps {
  variant: PriceButtonVariant
  direction: Direction
  currencyPair: CurrencyPair
  isExpired: boolean
  price?: number
}

export const PriceButtonInner = ({
  variant,
  direction,
  currencyPair,
  isExpired,
  price,
}: PriceButtonInnerProps) => {
  const { t } = useTranslation()
  return (
    <Button
      variant={variant}
      direction={direction}
      onClick={() => sendExecution(currencyPair.symbol, direction)}
      data-testid={`${direction}-${currencyPair.symbol}`}
    >
      {price && (
        <>
          <Symbol variant={variant}>
            {`${direction} ${currencyPair.base}`.toUpperCase()}
          </Symbol>
          <Price variant={variant} price={price} currencyPair={currencyPair} />
          {isExpired && (
            <p
              className="absolute bottom-auto flex h-full w-full items-center justify-center rounded-xs bg-primary text-lg-regular text-error-primary opacity-85"
              data-testid="expireLabel"
            >
              {t("liveRates.priceExpired")}
            </p>
          )}
        </>
      )}
    </Button>
  )
}
